import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Newspaper, ArrowUpRight, ArrowRight, TrendingUp, Percent, LineChart, Landmark, ChevronDown } from "lucide-react";
import { MaskLines, Reveal, Overline } from "../components/Motion";
import MarketTicker from "../components/MarketTicker";
import { ARTICLES, SOCIALS, PROCESS, TOPICS } from "../lib/data";

const TOPIC_ICONS = { TrendingUp, Percent, LineChart, Landmark };
const HERO_BG = "https://customer-assets-jt897jd0.emergentagent.net/job_vision-value-growth/artifacts/vtrzc0xo_5B758D0F-CC71-4AC1-877D-4A3E56C0E1C2.PNG";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden pb-20 pt-32" data-testid="hero-section">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
          <motion.img
            src={HERO_BG}
            alt="Financial skyline"
            className="w-full h-full object-cover"
            initial={{ scale: 1.05 }}
            animate={{ scale: 1.16 }}
            transition={{ duration: 24, ease: "easeInOut", repeat: Infinity, repeatType: "reverse" }}
          />
          <div className="absolute inset-0 bg-navy/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-navy/20" />
          <div className="absolute -bottom-40 -left-40 h-[32rem] w-[32rem] rounded-full bg-forest/20 blur-3xl" />
        </motion.div>

        <motion.div style={{ y: textY }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <span className="font-mono text-xs tracking-[0.22em] uppercase text-[#5FC08A]">Vision · Value · Growth</span>
          </motion.div>

          <h1 className="font-heading text-6xl sm:text-7xl lg:text-8xl xl:text-9xl leading-[0.92] tracking-tight text-cream mt-6 font-medium">
            <MaskLines lines={["Finance, explained", <span key="e" className="italic text-[#5FC08A]">ethically.</span>]} delay={0.3} />
          </h1>

          <Reveal delay={0.9} className="max-w-2xl mt-8">
            <p className="text-lg md:text-xl text-cream/80 leading-relaxed">
              Markets, tax, and money decisions — broken down clearly, for people who don't have a CA or a CFA on speed dial.
            </p>
          </Reveal>

          <Reveal delay={1.1} className="mt-10">
            <Link
              to="/insights"
              data-testid="hero-cta-hub"
              className="group inline-flex rounded-full bg-cream text-navy px-8 py-4 font-medium items-center justify-center gap-3 hover:bg-forest hover:text-cream transition-colors duration-300"
            >
              <Newspaper size={18} /> Explore the hub
            </Link>
          </Reveal>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          data-testid="hero-scroll-cue"
        >
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-cream/50">Scroll</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
            <ChevronDown className="text-cream/60" size={22} />
          </motion.div>
        </motion.div>
      </section>

      {/* LIVE MARKET TICKER */}
      <MarketTicker />

      {/* SECTION 1 — WHAT ETHIKALLY IS (dark) */}
      <section className="bg-navy text-cream py-24 md:py-36 relative z-10" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <span className="font-mono text-xs tracking-[0.22em] uppercase text-[#5FC08A]">What Ethikally is</span>
              <div className="font-mono text-[#5FC08A]/70 text-6xl mt-8">01</div>
            </div>
            <div className="md:col-span-8">
              <Reveal>
                <p className="font-heading text-3xl md:text-4xl lg:text-5xl leading-[1.25] text-cream">
                  Where professional rigour meets a <span className="text-[#5FC08A] italic">client-first approach</span> to finance.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="text-cream/70 text-lg leading-relaxed mt-10 max-w-3xl">
                  We are Chartered Accountants and CFA charterholders who have spent our careers on the other side of the table — understanding markets, underwriting risks and managing capital. Three principals anchor how this experience translates into our client work — <span className="text-cream font-medium">Vision, Value and Growth</span>.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — HOW WE WORK */}
      <section className="py-24 md:py-36 relative z-10" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <Overline>How we work</Overline>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl mt-4 tracking-tight">
              The machine, the method —
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {PROCESS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.08}>
                <div className="h-full border-t-2 border-forest pt-6" data-testid={`process-step-${p.num}`}>
                  <div className="font-mono text-forest text-sm">{p.num}</div>
                  <h3 className="font-heading text-2xl md:text-3xl mt-3 tracking-tight">{p.title}</h3>
                  <p className="text-navy/60 mt-3 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — WHAT WE BREAK DOWN (brand-green) */}
      <section className="bg-forest text-cream py-24 md:py-36 relative z-10" data-testid="topics-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="font-mono text-xs tracking-[0.22em] uppercase text-cream/70">What we break down</span>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl mt-4 tracking-tight max-w-3xl">
            Four lenses. One clear decision.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-cream/15 mt-16 border border-cream/15">
            {TOPICS.map((t, i) => {
              const Icon = TOPIC_ICONS[t.icon];
              return (
                <Reveal key={t.title} delay={i * 0.08}>
                  <div className="bg-forest p-8 h-full hover:bg-[#26663f] transition-colors duration-300" data-testid={`topic-${t.title.toLowerCase()}`}>
                    <Icon size={30} className="text-cream" strokeWidth={1.6} />
                    <h3 className="font-heading text-2xl md:text-3xl mt-6 tracking-tight">{t.title}</h3>
                    <p className="text-cream/70 mt-3 leading-relaxed">{t.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 4 — FEATURED (links to specific articles) */}
      <section className="py-24 md:py-36 relative z-10" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
              <Overline>Featured</Overline>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl mt-4 tracking-tight">Latest from the hub</h2>
            </div>
            <Link to="/insights" className="group flex items-center gap-2 text-navy font-medium hover:text-forest transition-colors" data-testid="featured-view-all">
              View all <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid md:grid-cols-12 gap-6 lg:gap-8">
            {ARTICLES.slice(0, 4).map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.08} className={i === 0 ? "md:col-span-8" : "md:col-span-4"}>
                <Link to={`/insights/${a.slug}`} className="group block h-full" data-testid={`featured-article-${a.slug}`}>
                  <div className={`overflow-hidden clip-frame ${i === 0 ? "h-72 md:h-96" : "h-56"}`}>
                    <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="mt-5">
                    <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-forest">
                      <span>{a.category}</span><span className="text-navy/30">/</span><span className="text-navy/40">{a.date}</span>
                    </div>
                    <h3 className={`font-heading mt-3 tracking-tight group-hover:text-forest transition-colors ${i === 0 ? "text-3xl md:text-4xl" : "text-2xl"}`}>
                      {a.title}
                    </h3>
                    <p className="text-navy/60 mt-2 leading-relaxed">{a.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOLLOW CTA */}
      <section className="py-28 md:py-40 relative z-10" data-testid="home-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <a
              href={SOCIALS.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="home-cta-follow"
              className="group inline-flex items-center gap-4"
            >
              <span className="font-heading text-6xl sm:text-7xl lg:text-8xl tracking-tight text-navy group-hover:text-forest transition-colors">
                Follow @ethikally
              </span>
              <ArrowUpRight size={64} className="text-forest group-hover:translate-x-2 group-hover:-translate-y-2 transition-transform" />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
